import { useEffect, useState } from "react";
import { orders } from "../lib/api";
import type { Order, OrderStats, Pagination } from "../lib/api";
import { ShoppingBag, RefreshCw, CheckCircle, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";

export default function Orders() {
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orderData, statsData] = await Promise.all([
        orders.list({ page, limit: 20, status: statusFilter || undefined }),
        orders.stats(),
      ]);
      setOrderList(orderData.orders);
      setPagination(orderData.pagination);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="card">
            <p className="text-gray-400 text-sm">Total Orders</p>
            <p className="text-2xl font-bold">{stats.totalOrders}</p>
          </div>
          <div className="card">
            <p className="text-gray-400 text-sm">Successful</p>
            <p className="text-2xl font-bold text-success">{stats.successfulOrders}</p>
          </div>
          <div className="card">
            <p className="text-gray-400 text-sm">Success Rate</p>
            <p className="text-2xl font-bold text-primary-500">{stats.successRate.toFixed(1)}%</p>
          </div>
          <div className="card">
            <p className="text-gray-400 text-sm">Total Spent</p>
            <p className="text-2xl font-bold">${stats.totalSpent.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Order History</h2>
        <div className="flex gap-2">
          <select
            className="input w-40"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          <button onClick={fetchData} className="btn-secondary">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Order List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-primary-500" size={32} />
        </div>
      ) : orderList.length === 0 ? (
        <div className="card text-center py-12">
          <ShoppingBag size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No orders yet.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Product</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Retailer</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Order ID</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Total</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {orderList.map((order) => (
                <tr key={order.id} className="hover:bg-dark-700/50">
                  <td className="px-4 py-3 text-sm">
                    {format(new Date(order.createdAt), "MMM d, HH:mm")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="max-w-xs truncate" title={order.product.name}>
                      {order.product.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">{order.retailer}</td>
                  <td className="px-4 py-3 font-mono text-sm">{order.orderId || "-"}</td>
                  <td className="px-4 py-3">
                    {order.status === "success" ? (
                      <span className="flex items-center gap-1 text-success text-sm">
                        <CheckCircle size={14} /> Success
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-error text-sm" title={order.errorMessage}>
                        <XCircle size={14} /> Failed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {order.totalPrice ? `$${order.totalPrice.toFixed(2)}` : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{(order.duration / 1000).toFixed(2)}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-50"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-gray-400">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
            className="btn-secondary disabled:opacity-50"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

